import { Link } from 'react-router-dom';
import { dataTableHeadClass } from '../../components/DataTablePanel';
import {
  Badge,
  Button,
  Callout,
  Card,
  CheckIcon,
  ListEmptyState,
  Modal,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalTitle,
  Switch,
  XIcon,
} from '../../components/ui';
import { t } from '../../i18n';
import { MoneyTotals } from '../../types/customer';
import { AutoAllocatePreview } from '../../types/payment';
import {
  bucketsFromTotals,
  fromCents,
  MoneyBuckets,
  toCents,
} from '../../utils/allocationMatch';
import { formatDate, formatMoney } from '../../utils/format';
import { selectionKey } from './useAutoAllocate';

export function AutoAllocatePreviewModal({
  isOpen,
  preview,
  applying,
  selectedKeys,
  previewKeys,
  allSelected,
  someSelected,
  hideCustomer,
  allowRematch,
  replaceExisting,
  previewing,
  onClose,
  onToggle,
  onSelectAll,
  onApply,
  onReplaceExistingChange,
}: {
  isOpen: boolean;
  preview: AutoAllocatePreview | null;
  applying: boolean;
  selectedKeys: Set<string>;
  previewKeys: string[];
  allSelected: boolean;
  someSelected: boolean;
  hideCustomer?: boolean;
  allowRematch?: boolean;
  replaceExisting?: boolean;
  previewing?: boolean;
  onClose: () => void;
  onToggle: (key: string) => void;
  onSelectAll: (keys: string[]) => void;
  onApply: () => void;
  onReplaceExistingChange?: (next: boolean) => void;
}) {
  const labels = t().reports;
  const remaining = preview
    ? remainingAfterPreview(preview, selectedKeys)
    : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl">
      <ModalHeader>
        <ModalTitle>{labels.autoAllocatePreviewTitle}</ModalTitle>
      </ModalHeader>
      <ModalContent className="space-y-4">
        <p className="text-sm text-ink-muted">{labels.autoAllocatePreviewHint}</p>
        {allowRematch ? (
          <div className="rounded-lg border border-line bg-surface-muted/40 p-3">
            <Switch
              checked={Boolean(replaceExisting)}
              disabled={applying || previewing}
              onChange={(e) => onReplaceExistingChange?.(e.target.checked)}
              label={labels.autoAllocateRematch}
              description={labels.autoAllocateRematchHint}
              size="sm"
            />
          </div>
        ) : null}
        {replaceExisting ? (
          <Callout variant="warning">{labels.autoAllocateConfirmRematch}</Callout>
        ) : null}
        {preview && remaining ? (
          <>
            <div className="grid gap-3 sm:grid-cols-3">
              <StatCard
                title={labels.autoAllocateMatched}
                value={String(preview.matched)}
              />
              <StatCard
                title={labels.autoAllocateLinks}
                value={String(preview.linkCount)}
              />
              <StatCard
                title={labels.autoAllocateUnmatched}
                value={String(preview.unmatched)}
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <SummaryCard
                title={labels.allocatedAmount}
                totals={remaining.selectedAllocated}
                emphasize
              />
              <SummaryCard
                title={labels.autoAllocateRemainingAfter}
                totals={remaining.leftoverAfter}
              />
            </div>
            <p className="text-sm text-ink-muted">
              {labels.autoAllocateSelected}: {selectedKeys.size} /{' '}
              {preview.linkCount}
            </p>

            {preview.links.length === 0 ? (
              <ListEmptyState title={labels.autoAllocateNone} />
            ) : (
              <div className="overflow-x-auto rounded-lg border border-line">
                <table className="w-max min-w-full text-sm">
                  <thead className={dataTableHeadClass}>
                    <tr>
                      <th className="w-10 px-3 py-2">
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-brand"
                          checked={allSelected}
                          ref={(el) => {
                            if (el) {
                              el.indeterminate = someSelected && !allSelected;
                            }
                          }}
                          onChange={() =>
                            onSelectAll(allSelected ? [] : previewKeys)
                          }
                          aria-label={labels.autoAllocateSelected}
                        />
                      </th>
                      {hideCustomer ? null : (
                        <th className="px-3 py-2 text-left font-semibold">
                          {t().common.customer}
                        </th>
                      )}
                      <th className="px-3 py-2 text-left font-semibold">
                        {labels.autoAllocatePayment}
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        {t().invoices.invoiceNo}
                      </th>
                      <th className="px-3 py-2 text-left font-semibold">
                        {t().invoices.invoiceDate}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        {labels.autoAllocateAmount}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        {labels.autoAllocatePaymentRemaining}
                      </th>
                      <th className="px-3 py-2 text-right font-semibold">
                        {labels.autoAllocateInvoiceRemaining}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.links.flatMap((link) => {
                      const split = link.allocations.length > 1;
                      return link.allocations.map((row, index) => {
                        const key = selectionKey(link.paymentId, row.invoiceId);
                        const invoiceAmt = Number(row.invoiceAmount);
                        const cashAmt = Number(row.cashAmount);
                        const showSplit =
                          invoiceAmt > 0.0001 && cashAmt > 0.0001;
                        return (
                          <tr
                            key={key}
                            className={`border-t border-line ${
                              split
                                ? 'bg-amber-50/70 dark:bg-amber-950/25'
                                : ''
                            }`}
                          >
                            <td className="px-3 py-2">
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-brand"
                                checked={selectedKeys.has(key)}
                                onChange={() => onToggle(key)}
                                aria-label={`${labels.autoAllocatePayment} ${link.paymentId} ${row.invoiceNo}`}
                              />
                            </td>
                            {hideCustomer ? null : (
                              <td className="max-w-[12rem] truncate px-3 py-2">
                                {index === 0 ? link.customer.name : ''}
                              </td>
                            )}
                            <td className="max-w-[16rem] px-3 py-2">
                              {index === 0 ? (
                                <div>
                                  <Link
                                    to={`/payments/${link.paymentId}`}
                                    className="block truncate text-brand hover:underline"
                                  >
                                    {link.description ||
                                      `Πληρωμή #${link.paymentId}`}
                                  </Link>
                                  <div className="text-xs text-ink-muted">
                                    {formatDate(link.paymentDate)}
                                    {' · '}
                                    {formatMoney(link.paymentTotals.total)}
                                  </div>
                                  {split ? (
                                    <Badge variant="warning" className="mt-1">
                                      {labels.autoAllocateSplit}
                                    </Badge>
                                  ) : null}
                                </div>
                              ) : null}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              <Link
                                to={`/invoices/${row.invoiceId}`}
                                className="font-medium text-brand hover:underline"
                              >
                                {row.invoiceNo}
                              </Link>
                            </td>
                            <td className="whitespace-nowrap px-3 py-2">
                              {formatDate(row.invoiceDate)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right">
                              <div className="tabular-nums font-medium">
                                {formatMoney(row.total)}
                              </div>
                              {showSplit ? (
                                <div className="text-xs text-ink-muted">
                                  {t().financial.invoice}{' '}
                                  {formatMoney(row.invoiceAmount)}
                                  {' · '}
                                  {t().financial.cash}{' '}
                                  {formatMoney(row.cashAmount)}
                                </div>
                              ) : null}
                            </td>
                            {index === 0 ? (
                              <RemainingCell
                                totals={remaining.paymentRemaining.get(
                                  link.paymentId,
                                )}
                              />
                            ) : (
                              <td className="px-3 py-2" />
                            )}
                            <RemainingCell
                              totals={remaining.invoiceRemaining.get(
                                row.invoiceId,
                              )}
                            />
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {preview.unmatchedPayments.length > 0 ? (
              <div>
                <h3 className="mb-2 text-sm font-semibold text-ink">
                  {labels.autoAllocateUnmatchedTitle}
                </h3>
                <div className="overflow-x-auto rounded-lg border border-line">
                  <table className="w-max min-w-full text-sm">
                    <thead className={dataTableHeadClass}>
                      <tr>
                        <th className="px-3 py-2 text-left font-semibold">
                          {t().common.date}
                        </th>
                        {hideCustomer ? null : (
                          <th className="px-3 py-2 text-left font-semibold">
                            {t().common.customer}
                          </th>
                        )}
                        <th className="px-3 py-2 text-left font-semibold">
                          {labels.autoAllocatePayment}
                        </th>
                        <th className="px-3 py-2 text-right font-semibold">
                          {t().financial.grandTotal}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.unmatchedPayments.map((row) => (
                        <tr
                          key={row.paymentId}
                          className="border-t border-line"
                        >
                          <td className="whitespace-nowrap px-3 py-2">
                            {formatDate(row.paymentDate)}
                          </td>
                          {hideCustomer ? null : (
                            <td className="max-w-[12rem] truncate px-3 py-2">
                              {row.customer.name}
                            </td>
                          )}
                          <td className="max-w-[12rem] truncate px-3 py-2">
                            <Link
                              to={`/payments/${row.paymentId}`}
                              className="text-brand hover:underline"
                            >
                              {row.description || `Πληρωμή #${row.paymentId}`}
                            </Link>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                            {formatMoney(row.paymentTotals.total)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </ModalContent>
      <ModalFooter>
        <Button
          type="button"
          variant="outline"
          disabled={applying}
          onClick={onClose}
        >
          <XIcon className="h-4 w-4" />
          {t().common.cancel}
        </Button>
        <Button
          type="button"
          loading={applying}
          disabled={!preview || selectedKeys.size === 0 || applying || previewing}
          onClick={onApply}
        >
          <CheckIcon className="h-4 w-4" />
          {labels.autoAllocateApply}
          {selectedKeys.size > 0 ? ` (${selectedKeys.size})` : ''}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function remainingAfterPreview(
  preview: AutoAllocatePreview,
  selectedKeys: Set<string>,
) {
  const paymentTaken = new Map<number, MoneyBuckets>();
  const invoiceTaken = new Map<number, MoneyBuckets>();
  const invoiceStart = new Map<number, MoneyBuckets>();

  for (const link of preview.links) {
    for (const row of link.allocations) {
      if (row.invoiceOutstanding && !invoiceStart.has(row.invoiceId)) {
        invoiceStart.set(
          row.invoiceId,
          bucketsFromTotals(row.invoiceOutstanding),
        );
      }
      const key = selectionKey(link.paymentId, row.invoiceId);
      if (!selectedKeys.has(key)) continue;
      const take = {
        invoice: toCents(row.invoiceAmount),
        cash: toCents(row.cashAmount),
      };
      addBucketsInto(paymentTaken, link.paymentId, take);
      addBucketsInto(invoiceTaken, row.invoiceId, take);
    }
  }

  const paymentRemaining = new Map<number, MoneyTotals>();
  let leftoverInvoice = 0;
  let leftoverCash = 0;
  for (const link of preview.links) {
    const rem = leftoverBuckets(
      bucketsFromTotals(link.paymentTotals),
      paymentTaken.get(link.paymentId),
    );
    leftoverInvoice += rem.invoice;
    leftoverCash += rem.cash;
    paymentRemaining.set(link.paymentId, totalsFromBuckets(rem));
  }

  const invoiceRemaining = new Map<number, MoneyTotals>();
  Array.from(invoiceStart.entries()).forEach(([invoiceId, start]) => {
    invoiceRemaining.set(
      invoiceId,
      totalsFromBuckets(leftoverBuckets(start, invoiceTaken.get(invoiceId))),
    );
  });

  let allocatedInvoice = 0;
  let allocatedCash = 0;
  Array.from(paymentTaken.values()).forEach((take) => {
    allocatedInvoice += take.invoice;
    allocatedCash += take.cash;
  });

  return {
    paymentRemaining,
    invoiceRemaining,
    selectedAllocated: totalsFromBuckets({
      invoice: allocatedInvoice,
      cash: allocatedCash,
    }),
    leftoverAfter: totalsFromBuckets({
      invoice: leftoverInvoice,
      cash: leftoverCash,
    }),
  };
}

function addBucketsInto(
  map: Map<number, MoneyBuckets>,
  id: number,
  take: MoneyBuckets,
) {
  const prev = map.get(id) ?? { invoice: 0, cash: 0 };
  map.set(id, {
    invoice: prev.invoice + take.invoice,
    cash: prev.cash + take.cash,
  });
}

function leftoverBuckets(
  start: MoneyBuckets,
  taken: MoneyBuckets | undefined,
): MoneyBuckets {
  return {
    invoice: Math.max(0, start.invoice - (taken?.invoice ?? 0)),
    cash: Math.max(0, start.cash - (taken?.cash ?? 0)),
  };
}

function totalsFromBuckets(buckets: MoneyBuckets): MoneyTotals {
  return {
    invoice: fromCents(buckets.invoice),
    cash: fromCents(buckets.cash),
    total: fromCents(buckets.invoice + buckets.cash),
  };
}

function RemainingCell({ totals }: { totals: MoneyTotals | undefined }) {
  if (!totals) {
    return (
      <td className="whitespace-nowrap px-3 py-2 text-right text-ink-muted">
        —
      </td>
    );
  }
  const invoiceAmt = Number(totals.invoice);
  const cashAmt = Number(totals.cash);
  const showSplit = invoiceAmt > 0.0001 && cashAmt > 0.0001;
  const isZero = Number(totals.total) < 0.0001;
  return (
    <td className="whitespace-nowrap px-3 py-2 text-right">
      <div
        className={`tabular-nums font-medium ${isZero ? 'text-ink-muted' : ''}`}
      >
        {formatMoney(totals.total)}
      </div>
      {showSplit ? (
        <div className="text-xs text-ink-muted">
          {t().financial.invoice} {formatMoney(totals.invoice)}
          {' · '}
          {t().financial.cash} {formatMoney(totals.cash)}
        </div>
      ) : null}
    </td>
  );
}

function StatCard({ title, value }: { title: string; value: string }) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 text-lg font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

function SummaryCard({
  title,
  totals,
  emphasize,
}: {
  title: string;
  totals: { invoice: string; cash: string; total: string };
  emphasize?: boolean;
}) {
  return (
    <Card className="p-4 text-sm">
      <div className="font-semibold text-ink">{title}</div>
      <div className="mt-3 space-y-1">
        <div className="flex justify-between">
          <span>{t().financial.invoice}</span>
          <span className="tabular-nums">{formatMoney(totals.invoice)}</span>
        </div>
        <div className="flex justify-between">
          <span>{t().financial.cash}</span>
          <span className="tabular-nums">{formatMoney(totals.cash)}</span>
        </div>
        <div
          className={`flex justify-between font-semibold ${emphasize ? 'text-brand' : ''}`}
        >
          <span>{t().financial.grandTotal}</span>
          <span className="tabular-nums">{formatMoney(totals.total)}</span>
        </div>
      </div>
    </Card>
  );
}
